import {
  BaseSyntheticEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import useModal from '../../../hooks/use-close-modal.hook';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/forms/input.component';
import Select from 'react-select';
import Title from '../../../components/forms/titles.component';
import InvalidFeedback from '../../../components/forms/invalid-feedback.component';
import { PROFILES } from '../../../constants';
import { RolesService } from '../../../services/roles.service';
import { AlertService } from '../../../services/alert.service';
import { useSession } from '../../../hooks/use-session.hook';
import { AccountsService } from '../../../services/accounts.service';
import useRequest from '../../../hooks/use-request.hook';
import LoaderModalTable from '../../../components/loader/loader-table-modal.component';
import LogData from '../../../components/logs/log-data.component';
import Icon from '../../../components/forms/icon.component';
import { CompaniesService } from '../../../services/companies.service';
import Button from '../../../components/forms/button.component';
import { useLocation, useNavigate } from 'react-router-dom';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import Navigate from '../../../components/cards/header-navigate.component';
interface AddRolesProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
}

const AddRoles = forwardRef(({ show, onRequestClose }: AddRolesProps, ref) => {
  useBgPages();
  const { t } = useTranslation();
  const { call, isRequesting } = useRequest();
  const location = useLocation();
  const payload = location.state?.payload;
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);
  const [companies, setCompanies] = useState<any>([]);
  const [accounts, setAccounts] = useState<any>([]);
  const [permissions, setPermissions] = useState<
    Record<string, { read: boolean; write?: boolean }>
  >({});
  const { session, selectedAccount } = useSession();

  const profilesLabel = {
    [PROFILES.ROOT]: t('roles.rol-root'),
    [PROFILES.STANDARD_USER]: t('roles.rol-standard-user')
  };

  let subtitleText;

  if (payload) {
    if (!payload.account) {
      subtitleText = 'ROOT';
    } else {
      subtitleText = `${payload.account.company.name} - ${payload.account.name}`;
    }
  }

  const permissionTranslations: Record<string, string> = {
    accounts: t('asaid.link-accounts'),
    reports: t('asaid.link-title-reports'),
    roles: t('asaid.link-roles'),
    users: t('asaid.link-users'),
    blacklist: t('asaid.link-blacklist'),
    campaigns: t('campaigns.send-campaigns'),
    templates: t('asaid.link-templates'),
    dashboard: t('asaid.link-dashboard'),
    channels: t('asaid.link-menu-channels')
  };
  const moduleIcons: Record<string, string> = {
    accounts: 'ki-duotone ki-user ',
    reports: 'ki-duotone ki-document',
    roles: 'ki-duotone ki-colors-square',
    users: 'ki-duotone ki-profile-user',
    blacklist: 'ki-duotone ki-shield',
    campaigns: 'ki-duotone ki-send',
    templates: 'ki-duotone ki-picture',
    dashboard: 'ki-duotone ki-dashboard',
    channels: 'ki-duotone ki-technology-4'
  };

  useImperativeHandle(ref, () => ({
    closeModal,
    submit: (e: BaseSyntheticEvent | undefined) =>
      handleSubmit((data) => {
        const buttonID = watch('_id') ? 'editRole-7326478503' : 'addRole-3394857490';
        return submit(data, buttonID);
      })(e)
  }));

  const [options, setOptions] = useState([
    { value: PROFILES.ROOT.toUpperCase(), label: t('roles.rol-root') },
    { value: PROFILES.STANDARD_USER.toUpperCase(), label: t('roles.rol-standard-user') }
  ]);
  const defaultValues = {
    _id: '',
    name: '',
    profile: '',
    account: '',
    company_id: '',
    permissions: {}
  };

  const {
    register,
    control,
    handleSubmit,
    getValues,
    reset,
    watch,
    setValue,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const hasAtLeastOneReadPermission = () => {
    return Object.values(permissions).some((perm) => perm.read);
  };

  const getModuleRoles = async () => {
    if (payload) return;
    try {
      const profile = watch('profile');
      const response = await call(() => RolesService.getModuleRoles({ profile }));

      if (response) {
        const updatedPermissions = Object.entries(response).reduce(
          (acc, [key, value]) => {
            acc[key] = { ...(value as any), write: false };
            return acc;
          },
          {} as Record<string, { read: boolean; write?: boolean }>
        );

        setPermissions(updatedPermissions);
        reset((prevValues) => ({
          ...prevValues,
          profile,
          permissions: updatedPermissions
        }));
      }
    } catch (errorCode: any) {
      console.log('Error: ', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getCompanies = async () => {
    const data = await CompaniesService.getCompanies();
    if (data) {
      const mappedCompanies = data?.map((item: any) => ({
        value: item._id,
        label: item.name
      }));
      setCompanies(mappedCompanies);
    } else {
      setCompanies([]);
    }
  };

  const getAccounts = async () => {
    try {
      const data = await AccountsService.getAccountsForCompany(getValues('company_id'));
      if (data) {
        const mappedAccounts = data?.map((item: any) => ({
          value: item._id,
          label: item.name
        }));
        setAccounts(mappedAccounts);
      }
    } catch (errorCode: any) {
      console.log('Error: ', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const submit = async (data: any, buttonId: string) => {
    const isRoot = data?.profile === PROFILES.ROOT;
    let message = '';
    let messageQuestion = '';
    if (isRoot) {
      message = '';
    } else {
      const selectedCompany = companies?.find(
        (company: any) => company.value === data.company
      )?.label;
      const selectedProfile = profilesLabel[data?.profile?.toUpperCase()] || t('general.no-info');

      const fields = [
        { label: t('roles.name-label'), value: data?.name },
        { label: t('roles.profile-label'), value: selectedProfile },
        { label: t('roles.company-label'), value: selectedCompany }
      ];
      message = fields
        .filter((field) => field.value)
        ?.map((field) => `<strong>${field.label}</strong>: ${field.value}`)
        .join('<br>');
    }
    if (!payload) {
      messageQuestion = t('roles.send-alert');
    } else {
      messageQuestion = t('roles.edit-alert');
    }
    const isConfirmed = await AlertService.question(
      t('general.atention'),
      messageQuestion + '<br><br>' + message,
      () => Promise.resolve(true)
    );
    if (!isConfirmed) {
      return;
    } else {
      try {
        data = RequestBody(data);
        !payload ? await RolesService.saveRoles(data) : await RolesService.editRoles(data);
        reset();
        onRequestClose(true);
        AlertService.toastSuccess(!payload ? t('roles.send-success') : t('roles.edit-success'));
        LogData(data, buttonId, window.location.href);
        navigate('/roles');
      } catch (errorCode: any) {
        console.log('Error: ', errorCode);
        AlertService.toastError(errorCode.error || errorCode);
      }
    }
  };

  const handlePermissionChange = (module: string, type: 'read' | 'write', value: boolean) => {
    setPermissions((prev) => {
      const updatedModule = { ...prev[module], [type]: value };
      if (type === 'read' && !value) {
        updatedModule.write = false;
        setValue(`permissions.${module}.write` as any, false);
      }
      return { ...prev, [module]: updatedModule };
    });
    setValue(`permissions.${module}.${type}` as any, value);
  };

  const closeModal = () => {
    reset(defaultValues);
    onRequestClose(false);
  };

  const RequestBody = (data: any) => {
    const newData = { ...data };
    if (!payload) delete newData._id;
    delete newData.company_id;
    newData.profile = newData.profile?.toUpperCase();
    data.profile == PROFILES.ROOT.toUpperCase() && delete newData.account;
    return newData;
  };

  useEffect(() => {
    session.profile === PROFILES.ROOT.toUpperCase() && getCompanies();
    session.profile !== PROFILES.ROOT.toUpperCase() &&
      setOptions(options.filter((option) => option.value !== PROFILES.ROOT.toUpperCase()));
  }, []);

  useEffect(() => {
    if (session.profile === PROFILES.ADMIN && selectedAccount?.account_id) {
      setValue('account', selectedAccount.account_id);
    }
  }, [session.profile, selectedAccount]);

  useEffect(() => {
    watch('company_id') && getAccounts();
    watch('profile') && getModuleRoles();
  }, [watch('profile'), session.profile, watch('company_id')]);

  useEffect(() => {
    if (payload) {
      setPermissions(payload.permissions ?? {});
      reset({
        _id: payload?._id ?? defaultValues._id,
        name: payload?.name ?? defaultValues.name,
        profile: payload?.profile ?? defaultValues.profile,
        account: payload?.account?._id ?? defaultValues.account,
        company_id: payload?.account?.company?._id ?? defaultValues.company_id,
        permissions: payload?.permissions ?? defaultValues.permissions
      });
    }
  }, [payload, reset]);

  return (
    <div className="row gy-5 g-xl-10" ref={modalRef} data-bs-backdrop="static">
      <Navigate
        title={payload ? t('roles.btn-edit-modal') : t('roles.btn-add-modal')}
        titleBolder={payload ? payload?.name : t('roles.roles-title')}
        subtitle={subtitleText ?? 'Roles'}
        breadcrumbItems={[
          t('asaid.link-roles'),
          !payload ? t('general.register') : t('general.edit')
        ]}
        btnBack
      />
      <div>
        <div className="modal-content">
          <form
            id="addRolesForm"
            onSubmit={(e) => {
              e.preventDefault();
              const buttonID = watch('_id') ? 'editRole-7326478503' : 'addRole-3394857490';
              handleSubmit((data) => submit(data, buttonID))(e);
            }}
          >
            <div className="row gy-5 g-xl-10">
              <div className="col-lg-4 mt-5 me-lg-10 h-100 card p-10">
                <Title
                  icon={'ki-notepad-edit'}
                  title={t('roles.title-data-roles')}
                  subtitle={t('roles.text-data-roles')}
                />
                <div className="col-lg-12">
                  {!payload && (
                    <div className="fv-row">
                      <label className="form-label required">{t('roles.profile-label')}</label>
                      <Controller
                        control={control}
                        name="profile"
                        rules={{ required: true }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <>
                            <Select
                              options={options}
                              isDisabled={payload ? true : false}
                              noOptionsMessage={({ inputValue }) =>
                                inputValue
                                  ? t('general.no-options', { inputValue })
                                  : t('general.no-options')
                              }
                              className={
                                error ? 'form-select-custom is-invalid' : 'form-select-custom'
                              }
                              classNamePrefix="form-select-custom"
                              placeholder={t('roles.profile-placeholder')}
                              onChange={(selectedOption) => {
                                onChange(selectedOption ? selectedOption.value : null);
                                setValue('profile', selectedOption ? selectedOption.value : '');
                              }}
                              value={options.find((option) => option.value === value) || null}
                            />
                            <InvalidFeedback error={error} />
                          </>
                        )}
                      />
                    </div>
                  )}
                </div>
                {session.profile === PROFILES.ROOT &&
                  watch('profile') &&
                  watch('profile') !== PROFILES.ROOT && (
                    <>
                      {/* Empresa */}
                      <div className="col-lg-12">
                        <div className="fv-row">
                          <label className="form-label required">{t('roles.company-label')}</label>
                          <Controller
                            control={control}
                            name="company_id"
                            rules={{ required: true }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                              <>
                                <Select
                                  isDisabled={watch('profile') === ''}
                                  options={companies}
                                  noOptionsMessage={({ inputValue }) =>
                                    inputValue
                                      ? t('general.no-options', { inputValue })
                                      : t('general.no-options')
                                  }
                                  className={
                                    error ? 'form-select-custom is-invalid' : 'form-select-custom'
                                  }
                                  classNamePrefix="form-select-custom"
                                  placeholder={t('roles.company-placeholder')}
                                  onChange={(selectedOption) => {
                                    onChange(selectedOption ? selectedOption.value : null);
                                    setValue(
                                      'company_id',
                                      selectedOption ? selectedOption.value : ''
                                    );
                                  }}
                                  value={
                                    companies?.find((company: any) => company.value === value) || {
                                      value,
                                      label: value
                                    }
                                  }
                                />
                                <InvalidFeedback error={error} />
                              </>
                            )}
                          />
                        </div>
                      </div>

                      {/* Cuenta */}
                      <div className="col-lg-12">
                        <div className="fv-row">
                          <label className="form-label required">{t('roles.account-label')}</label>
                          <Controller
                            control={control}
                            name="account"
                            rules={{ required: true }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                              <>
                                <Select
                                  options={accounts}
                                  isDisabled={watch('company_id') === ''}
                                  noOptionsMessage={({ inputValue }) =>
                                    inputValue
                                      ? t('general.no-options', { inputValue })
                                      : t('general.no-options')
                                  }
                                  className={
                                    error ? 'form-select-custom is-invalid' : 'form-select-custom'
                                  }
                                  classNamePrefix="form-select-custom"
                                  placeholder={t('roles.account-placeholder')}
                                  onChange={(selectedOption) => {
                                    onChange(selectedOption ? selectedOption.value : null);
                                    setValue('account', selectedOption ? selectedOption.value : '');
                                  }}
                                  value={
                                    accounts?.find((account: any) => account.value === value) ||
                                    null
                                  }
                                />
                                <InvalidFeedback error={error} />
                              </>
                            )}
                          />
                        </div>
                      </div>
                    </>
                  )}
                {/* Nombre */}
                {watch('profile') !== '' && (
                  <div className="col-lg-12">
                    <div className="fv-row">
                      <Input
                        label={t('roles.name-label')}
                        requiredIndicator="required"
                        placeholder={t('roles.name-placeholder')}
                        errors={errors.name}
                        {...register('name', {
                          required: true,
                          setValueAs: (value) => value.trim()
                        })}
                      />
                    </div>
                  </div>
                )}
                <div className="d-flex justify-content-end mt-5">
                  <Button
                    type="submit"
                    id="sendData"
                    className="btn btn-primary hover-scale w-20"
                    disabled={!isValid || !hasAtLeastOneReadPermission()}
                    isLoading={isSubmitting}
                  >
                    {t('general.save')} <Icon icon="ki-send" size="fs-1" ClassName="ms-2" />
                  </Button>
                </div>
              </div>
              {/* TABLA */}
              <div className="col mt-5 h-100 p-10 card">
                <div></div>
                {watch('profile') ? (
                  !isRequesting ? (
                    <>
                      <Title
                        type={'info'}
                        required={true}
                        title={t('roles.permissions-title')}
                        subtitle={t('roles.permissions-text')}
                        icon="ki-gear"
                      />
                      <div className="overflow-scroll">
                        <div className="table-responsive">
                          <table className="table table-thead-bordered table-nowrap table-align-middle table-first-col-px-0">
                            <thead className="thead-light">
                              <tr>
                                <th>
                                  <strong>{t('roles.module')}</strong>
                                </th>
                                <th className="text-center">
                                  <Icon icon="ki-eye" size="fs-2" />
                                  <p className="m-0">
                                    <strong>{t('roles.read')}</strong>
                                  </p>
                                </th>
                                <th className="text-center">
                                  <Icon icon="ki-plus-square" size="fs-2" />

                                  <p className="m-0">
                                    <strong>{t('roles.write')}</strong>
                                  </p>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(permissions).map(([module, values]) => (
                                <tr key={module}>
                                  <td>
                                    <div className="d-flex justify-content-between align-items-center w-100">
                                      <div className="d-flex align-items-center">
                                        <span className="menu-icon">
                                          <Icon
                                            icon={`${moduleIcons[module] || 'ki-duotone ki-settings'}`}
                                            size="fs-2x"
                                            type={`${values.read ? 'primary' : ''}`}
                                          />
                                        </span>
                                        <span className="ms-2 text-nowrap">
                                          {permissionTranslations[module] || module}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="align-middle text-center">
                                    <div className="form-check form-check-inline m-0">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={values.read || false}
                                        onChange={(e) => {
                                          handlePermissionChange(module, 'read', e.target.checked);
                                          setValue(
                                            `permissions.${module}.read` as any,
                                            e.target.checked
                                          );
                                        }}
                                      />
                                    </div>
                                  </td>
                                  <td className="align-middle text-center">
                                    <div className="form-check form-check-inline m-0">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={values.write || false}
                                        onChange={(e) => {
                                          handlePermissionChange(module, 'write', e.target.checked);
                                          setValue(
                                            `permissions.${module}.write` as any,
                                            e.target.checked
                                          );
                                        }}
                                        disabled={!values.read}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <LoaderModalTable />
                  )
                ) : (
                  <>
                    <div className="relative">
                      <div className="text-center p-4">
                        {' '}
                        <img
                          src="/assets/media/illustrations/undraw/loading.svg"
                          alt="warning"
                          width="50%"
                        />
                        <h1 className="mb-0 fs-3 mt-10">{t('roles.select-profile-message')}</h1>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});
export default AddRoles;
